/* ==========================================================================
   CORDYGEN — extras.js (copy/share, offer codes, video stories)
   Vanilla JS, no dependencies. Loaded with `defer`.
   ========================================================================== */
(() => {
  'use strict';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const qs = (s, el = document) => el.querySelector(s);
  const qsa = (s, el = document) => Array.from(el.querySelectorAll(s));
  const toast = (msg) => {
    const el = qs('[data-toast]');
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
    clearTimeout(el._t);
    el._t = setTimeout(() => { el.hidden = true; }, 3200);
  };

  /* ==========================================================================
     Copy to clipboard (offer codes, product link) + native share
     ========================================================================== */
  const copyText = async (text) => {
    try { await navigator.clipboard.writeText(text); return true; }
    catch (e) {
      const ta = document.createElement('textarea');
      ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      let ok = false; try { ok = document.execCommand('copy'); } catch (err) { ok = false; }
      ta.remove(); return ok;
    }
  };
  document.addEventListener('click', async (e) => {
    const c = e.target.closest('[data-copy]');
    if (c) {
      const ok = await copyText(c.dataset.copy);
      if (ok) {
        toast(c.dataset.copied || 'Copied');
        c.classList.add('is-copied');
        setTimeout(() => c.classList.remove('is-copied'), 1800);
      }
      return;
    }
    const sh = e.target.closest('[data-native-share]');
    if (sh && navigator.share) {
      navigator.share({ title: sh.dataset.title, url: sh.dataset.url }).catch(() => {});
    }
  });
  if (navigator.share) qsa('[data-native-share]').forEach((b) => { b.hidden = false; });

  /* ==========================================================================
     Video stories — muted autoplay only while on screen, tap to play / unmute
     ========================================================================== */
  customElements.define('video-stories', class extends HTMLElement {
    connectedCallback() {
      this.cards = qsa('.vstory', this);
      const auto = this.dataset.autoplay === 'true' && !reduceMotion && 'IntersectionObserver' in window;
      this.cards.forEach((card) => {
        const v = qs('video', card);
        if (!v) return;
        v.muted = true;
        v.addEventListener('play', () => card.classList.add('is-playing'));
        v.addEventListener('pause', () => card.classList.remove('is-playing'));
        qs('[data-vs-play]', card).addEventListener('click', () => {
          if (v.paused) { card.dataset.userPaused = ''; this.play(v); } else { card.dataset.userPaused = '1'; v.pause(); }
        });
        v.addEventListener('click', () => { if (!v.paused) { card.dataset.userPaused = '1'; v.pause(); } });
        const snd = qs('[data-vs-sound]', card);
        snd.addEventListener('click', () => {
          const unmute = v.muted;
          this.cards.forEach((other) => { const ov = qs('video', other); if (ov && ov !== v) { ov.muted = true; this.setSound(other, false); } });
          v.muted = !unmute;
          this.setSound(card, unmute);
          if (unmute && v.paused) this.play(v);
        });
      });
      if (!auto) return;
      this.io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          const v = qs('video', en.target);
          if (!v) return;
          if (en.isIntersecting && en.intersectionRatio >= 0.6) {
            if (en.target.dataset.userPaused !== '1') this.play(v);
          } else if (!v.paused) { v.pause(); }
        });
      }, { threshold: [0, 0.6] });
      this.cards.forEach((c) => this.io.observe(c));
    }
    play(v) {
      if (v.preload === 'none') v.preload = 'auto';
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    }
    setSound(card, on) {
      const b = qs('[data-vs-sound]', card);
      if (!b) return;
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
      b.setAttribute('aria-label', on ? b.dataset.labelOn : b.dataset.labelOff);
      card.classList.toggle('has-sound', on);
    }
    disconnectedCallback() { if (this.io) this.io.disconnect(); }
  });

  /* ==========================================================================
     Floating video — small muted preview, tap to enlarge with sound
     ========================================================================== */
  customElements.define('floating-video', class extends HTMLElement {
    connectedCallback() {
      let closed = false;
      try { closed = sessionStorage.getItem('cg-fv-closed') === '1'; } catch (e) { closed = false; }
      if (closed && !window.Shopify?.designMode) return;
      this.hidden = false;
      const v = qs('video', this);
      if (!v) return;
      v.muted = true;
      if (this.dataset.autoplay === 'true' && !reduceMotion) { const p = v.play(); if (p && p.catch) p.catch(() => {}); }
      const snd = qs('[data-fv-sound]', this);
      const setSound = (on) => {
        v.muted = !on;
        this.classList.toggle('has-sound', on);
        if (snd) { snd.setAttribute('aria-pressed', on ? 'true' : 'false'); snd.setAttribute('aria-label', on ? snd.dataset.labelOn : snd.dataset.labelOff); }
      };
      qs('[data-fv-open]', this).addEventListener('click', () => {
        this.classList.add('is-open');
        v.currentTime = 0;
        setSound(true);
        const p = v.play(); if (p && p.catch) p.catch(() => {});
      });
      if (snd) snd.addEventListener('click', () => setSound(v.muted));
      qs('[data-fv-close]', this).addEventListener('click', () => {
        if (this.classList.contains('is-open')) {
          this.classList.remove('is-open');
          setSound(false);
          if (reduceMotion || this.dataset.autoplay !== 'true') v.pause();
          return;
        }
        v.pause();
        this.hidden = true;
        try { sessionStorage.setItem('cg-fv-closed', '1'); } catch (e) { /* ignore */ }
      });
      const add = qs('[data-fv-add]', this);
      if (add) add.addEventListener('click', () => {
        const main = qs('[data-main-buy] [data-add-button]');
        if (main && !main.disabled) { setSound(false); this.classList.remove('is-open'); main.click(); }
      });
    }
  });

  document.addEventListener('shopify:section:load', () => {
    if (navigator.share) qsa('[data-native-share]').forEach((b) => { b.hidden = false; });
  });
})();
